import { useRef, useCallback, useMemo, useEffect, useState } from 'react'

// import * as htmlToImage from 'html-to-image'

import { formatAmounts } from '@aytm/app/stats/lib/util/helpers'
import { pressable } from '@aytm/app/stats/components/lib/pressable'
import { isMapOrNone, processTextString } from '@aytm/app/stats/page/components/lib/utils'
import { removeLogicFromText, removeTagsFromText } from '@aytm/app/stats/page/components/lib/utils'

import { echoPointForCell } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/pointHalo'

import {
  getSigtestLetters,
  getSigtestStats,
  getCrosstabStats
} from '@aytm/app/stats/page/question-views/cq/components/partial/lib/stats'
import type {
  ItemsListProps,
  SigtestValue,
  AnswerStats
} from '@aytm/app/stats/page/question-views/cq/components/partial/types'

import { useComponentWidth } from '@aytm/app/stats/page/question-views/components/charts/lib/hooks'

import style from '@aytm/app/stats/page/question-views/cq/components/partial/styles/ItemsList.module.scss'

// First paint, before the grid reports its own box.
const DEFAULT_WIDTH = 814

export default function ItemsList({ model, data, blockId, onToggleLegend }: ItemsListProps) {
  const mode = model.mode ?? 'sigtest'
  const text = model.text
  const answerLabels = model.answerLabels
  const ignoreTotal = model.ignoreTotal
  const batches = model.batches
  const answer = model.answer

  const confidenceLevel = (data.confidenceLevel as number) ?? 0.95
  const significanceLevel = (data.significanceLevel as number) ?? 0.9

  const percentDecimals = model.percentDecimals ?? data.percentDecimals

  // the grid measures the box it already holds for the scroll handler
  const componentRef = useRef<HTMLDivElement>(null)
  const { width } = useComponentWidth({ defaultWidth: DEFAULT_WIDTH, ref: componentRef })

  const w = width - 22
  const dim = 3
  const size = Math.ceil(batches.length / dim)
  const d = size > 1 ? dim : batches.length
  const textFlex = 200
  const statFlex = (w - textFlex) / d

  const [scrollPosition, setScrollPosition] = useState(0)

  const headers = useMemo(() => model.headers?.table ?? ['amount', 'percents'], [model.headers])

  const popup = useMemo(
    () => model.headers?.popup?.map(v => [v, model.format?.popup?.[v] ?? v] as [string, string]) ?? [],
    [model.headers, model.format]
  )

  const sigtestLetters = useMemo(() => getSigtestLetters(model.batches), [model.batches])

  const sigtestStats = useMemo(
    () =>
      getSigtestStats(
        model.batches,
        { ...model.answer, confidenceLevel: model.confidenceLevel, significanceLevel: model.significanceLevel },
        sigtestLetters,
        confidenceLevel,
        significanceLevel
      ),
    [
      model.batches,
      model.answer,
      model.confidenceLevel,
      model.significanceLevel,
      sigtestLetters,
      confidenceLevel,
      significanceLevel
    ]
  )

  const crosstabStats = useMemo(
    () => getCrosstabStats(model.batches, { ...model.answer, confidenceLevel: model.confidenceLevel }, confidenceLevel),
    [model.batches, model.answer, model.confidenceLevel, confidenceLevel]
  )

  const chartModeOn = useMemo(() => !isMapOrNone(model.chartMode, data.chartMode), [model.chartMode, data.chartMode])

  const legendEnabled = useMemo(
    () => (model.legendEnabled === undefined || model.legendEnabled) && chartModeOn,
    [model.legendEnabled, chartModeOn]
  )

  // const onDownload = () => {
  //   htmlToImage
  //     .toPng(componentRef.current)
  //     .then(dataUrl => {
  //       const img = new Image()
  //       img.src = dataUrl
  //       document.body.appendChild(img)
  //     })
  //     .catch(err => {
  //       console.error('oops, something went wrong!', err)
  //     })
  // }

  const handleScroll = useCallback(() => {
    if (componentRef.current) {
      setScrollPosition(componentRef.current.scrollLeft)
    }
  }, [])

  useEffect(() => {
    const element = componentRef.current
    if (element) {
      element.scrollLeft = scrollPosition
    }
  }, [scrollPosition])

  const renderHeader = () => {
    if (!Array.isArray(text) || text.length !== 2) return null

    return (
      <div className={style['data-view-grid-row']}>
        <div
          className={style['data-view-grid-cell-header']}
          style={{ left: 0, width: w + 22, maxWidth: w + 22, paddingLeft: textFlex + 34 }}
          dangerouslySetInnerHTML={{ __html: removeLogicFromText(text[0]) }}
        />
      </div>
    )
  }

  const renderBatchHeader = (batch: (typeof batches)[number], batchIndex: number) => {
    const batchStats = (answer[`b${batch.position}`] ?? {}) as AnswerStats
    const newBatchText = processTextString(batch.text)
    const showTotal = batchStats.total && (ignoreTotal === undefined || !ignoreTotal)

    return (
      <div
        key={`batch-header-${batch.position}`}
        className={style['data-view-grid-cell-header']}
        style={{ width: statFlex, maxWidth: statFlex }}
      >
        <span className={style.title} data-testid="stat-dl-cq-column-title">
          {(answerLabels === undefined || answerLabels !== 'hide') && batch.prefix && (
            <>
              <b className={style.position}>{batch.prefix}:</b>{' '}
            </>
          )}
          <span dangerouslySetInnerHTML={{ __html: newBatchText }} />
          {showTotal && ` (n=${formatAmounts(batchStats, 'total')})`}
        </span>
        {['sigtest', 'both'].includes(mode) && sigtestLetters && (
          <div className={style['data-view-sigtestletter']} data-testid="stat-dl-cq-column-letter">
            {sigtestLetters[batchIndex] ?? ''}
          </div>
        )}
      </div>
    )
  }

  const renderCell = (
    batch: (typeof batches)[number],
    av: (typeof batches)[number]['answerVariants'][number],
    batchIndex: number,
    avIndex: number
  ) => {
    const batchStats = (answer[`b${batch.position}`] ?? {}) as Record<string, AnswerStats>
    const avStats = batchStats[`a${av.position}`]
    const sigtest = sigtestStats[batchIndex]?.[avIndex] ?? []
    const crosstab = crosstabStats[batchIndex]?.[avIndex]

    const title =
      avStats && popup.length > 0
        ? popup
            .map(
              ([key, label]) =>
                `${label}: ${key === 'percents' ? `${formatAmounts(avStats, key, 1)}%` : formatAmounts(avStats, key)}`
            )
            .join('\n')
        : ''

    const crosstabClass = ['crosstab', 'both'].includes(mode) && crosstab ? ` ${style[crosstab]}` : ''

    return (
      <div
        key={`cell-${batch.position}-${av.position}`}
        data-cell-position={`b${batch.position}a${av.position}`}
        className={`${style['data-view-grid-cell']}${crosstabClass}`}
        title={title}
        style={{ width: statFlex, maxWidth: statFlex }}
        onMouseEnter={() => echoPointForCell(blockId, `b${batch.position}a${av.position}`)}
        onMouseLeave={() => echoPointForCell(blockId, null)}
      >
        {avStats && (
          <>
            <span className={style['data-view-text']}>
              {formatAmounts(avStats, headers[0])}
              {headers.slice(1).map((header, idx) => (
                <span key={`header-${idx}`}>
                  <br />
                  {header === 'percents'
                    ? `${formatAmounts(avStats, header, percentDecimals)}%`
                    : `${header}: ${formatAmounts(avStats, header)}`}
                </span>
              ))}
            </span>
            {['sigtest', 'both'].includes(mode) && sigtest.length > 0 && (
              <span
                className={style['data-view-sigtest']}
                title={sigtest.map((s: SigtestValue) => s.letter ?? '').join(' ')}
              >
                {sigtest.map((sigtestValue: SigtestValue, idx: number) => (
                  <span
                    key={`sigtest-${idx}`}
                    className={style['data-view-sigtest-value']}
                    data-testid="stat-dl-cq-cell-letter"
                  >
                    {sigtestValue.letter ?? ''}
                  </span>
                ))}
              </span>
            )}
          </>
        )}
      </div>
    )
  }

  const renderRow = (av: (typeof batches)[number]['answerVariants'][number], avIndex: number) => {
    const newAvText = processTextString(av.text)

    return (
      <div key={`row-${av.position}`} className={style['data-view-grid-row']}>
        <div
          className={style['data-view-grid-cell-answer']}
          style={{ width: textFlex, maxWidth: textFlex }}
          data-testid="stat-dl-cq-row-label"
        >
          {(answerLabels === undefined || answerLabels !== 'hide') && av.prefix && (
            <b className={style.position}>{av.prefix}:</b>
          )}
          <span dangerouslySetInnerHTML={{ __html: newAvText }} />
        </div>
        {batches.map((batch, batchIndex) => renderCell(batch, av, batchIndex, avIndex))}
      </div>
    )
  }

  return (
    <div ref={componentRef} className={style['data-view']} data-testid="stat-dl-cq-av-list" onScroll={handleScroll}>
      {0 < batches.length ? (
        <div
          data-cf-block={blockId}
          data-testid="stat-dl-cq-grid"
          className={style['data-view-grid']}
          style={{ width: textFlex + statFlex * batches.length + 22 }}
        >
          {renderHeader()}

          <div className={style['data-view-grid-row']} style={{ flexWrap: 'nowrap', alignContent: 'stretch' }}>
            <div className={style['data-view-grid-cell-text']}>
              {Array.isArray(text) && 2 == text.length && (
                <span
                  title={removeTagsFromText(removeLogicFromText(text[1]))}
                  style={{
                    width: 42 * batches[0].answerVariants.length,
                    marginTop: 21 * batches[0].answerVariants.length
                  }}
                  dangerouslySetInnerHTML={{ __html: removeLogicFromText(text[1]) }}
                />
              )}
            </div>

            <div>
              <div className={style['data-view-grid-row']}>
                <div className={style['data-view-grid-cell-header']} style={{ width: textFlex, maxWidth: textFlex }}>
                  {legendEnabled ? (
                    <span
                      data-testid="stat-dl-cq-legend-toggle"
                      className={style.legend}
                      {...pressable(onToggleLegend)}
                    >
                      Legend
                    </span>
                  ) : (
                    '\u00A0'
                  )}
                </div>
                {batches.map((batch, batchIndex) => renderBatchHeader(batch, batchIndex))}
              </div>

              {batches[0]?.answerVariants.map((av, avIndex) => renderRow(av, avIndex))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
